import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { Link, useNavigate } from 'react-router-dom'
import { useBrand } from '../hooks/useBrand'

// ── Brand tokens — public page, no CSS vars ──────────────────────────────
const NAVY  = '#0B1F3A'
const TEAL  = '#2DD4B0'
const TEAL_D = '#1BA888'
const MUTED = '#6B7280'
const BORDER= '#E5E7EB'
const BG    = '#F8FAFC'
const GREEN = '#16A34A'
const RED   = '#DC2626'
const AMBER = '#D97706'

const STATUS_COLORS = {
  received:    { bg:'#EFF6FF', color:'#1D4ED8' },
  pending:     { bg:'#FFFBEB', color:'#92400E' },
  confirmed:   { bg:'#FFF7ED', color:'#C2410C' },
  processing:  { bg:'#F0FDFA', color:'#0F766E' },
  with_courier:{ bg:'#F5F3FF', color:'#6D28D9' },
  shipped:     { bg:'#F0FDF4', color:'#166534' },
  closed:      { bg:'#F9FAFB', color:'#6B7280' },
  return:      { bg:'#FEF2F2', color:'#991B1B' },
}
const STATUS_LABELS = {
  received:'Received', pending:'Pending', confirmed:'Confirmed',
  processing:'Processing', with_courier:'With Courier',
  shipped:'Shipped', closed:'Closed', return:'Returned',
}
const STATUS_STEPS = ['received','confirmed','processing','with_courier','shipped','closed']
const MANUAL_COURIERS  = ['Manual delivery','Self delivery','Friend / volunteer']
const PICKUP_COURIERS  = ['Customer pickup','Event / Fair pickup']

const COUNTRY_CODES = [
  { code:'+880', flag:'🇧🇩', name:'Bangladesh',   hint:'1XXXXXXXXX' },
  { code:'+61',  flag:'🇦🇺', name:'Australia',    hint:'4XXXXXXXX' },
  { code:'+44',  flag:'🇬🇧', name:'UK',           hint:'7XXXXXXXXX' },
  { code:'+1',   flag:'🇺🇸', name:'USA/Canada',   hint:'XXXXXXXXXX' },
  { code:'+971', flag:'🇦🇪', name:'UAE',          hint:'5XXXXXXXX' },
  { code:'+966', flag:'🇸🇦', name:'Saudi Arabia', hint:'5XXXXXXXX' },
  { code:'+60',  flag:'🇲🇾', name:'Malaysia',     hint:'1XXXXXXXXX' },
  { code:'+65',  flag:'🇸🇬', name:'Singapore',    hint:'8XXXXXXX' },
  { code:'+91',  flag:'🇮🇳', name:'India',        hint:'XXXXXXXXXX' },
  { code:'+92',  flag:'🇵🇰', name:'Pakistan',     hint:'3XXXXXXXXX' },
  { code:'+64',  flag:'🇳🇿', name:'New Zealand',  hint:'2XXXXXXXX' },
  { code:'+49',  flag:'🇩🇪', name:'Germany',      hint:'1XXXXXXXXX' },
  { code:'+33',  flag:'🇫🇷', name:'France',       hint:'6XXXXXXXXX' },
  { code:'+39',  flag:'🇮🇹', name:'Italy',        hint:'3XXXXXXXXX' },
  { code:'+81',  flag:'🇯🇵', name:'Japan',        hint:'9XXXXXXXXXX' },
  { code:'+82',  flag:'🇰🇷', name:'South Korea',  hint:'1XXXXXXXXX' },
  { code:'+other',flag:'🌍', name:'Other',        hint:'Number without leading 0' },
]

let pdfGenerating = false
function generateReceiptPDF(order, seller, brand) {
  if (pdfGenerating) return
  pdfGenerating = true
  import('jspdf').then(({ default: jsPDF }) => {
    const doc = new jsPDF({ unit:'mm', format:'a4' })
    const pw  = doc.internal.pageSize.getWidth()
    let y = 20
    const center = (t,yp,s=10) => { doc.setFontSize(s); doc.text(t,pw/2,yp,{align:'center'}) }
    const ln     = (yp) => { doc.setDrawColor(220,220,220); doc.line(20,yp,pw-20,yp) }
    const row    = (l,v,yp) => { doc.setFontSize(9);doc.setFont('helvetica','normal');doc.setTextColor(120,120,120);doc.text(l,20,yp);doc.setTextColor(40,40,40);doc.text(String(v),pw-20,yp,{align:'right'}) }
    doc.setFont('helvetica','bold');doc.setFontSize(18);doc.setTextColor(11,31,58)
    center(brand.name,y,18);y+=6;doc.setFont('helvetica','normal');doc.setFontSize(9);doc.setTextColor(120,120,120)
    center(brand.website||'pageconnect.io',y,9);y+=10;ln(y);y+=8
    doc.setFont('helvetica','bold');doc.setFontSize(14);doc.setTextColor(20,20,20)
    center(seller?.shop_name||'Seller',y,14);y+=6
    if(seller?.phone){doc.setFont('helvetica','normal');doc.setFontSize(9);doc.setTextColor(100,100,100);center(seller.phone,y,9);y+=5}
    y+=4;ln(y);y+=8
    doc.setFont('helvetica','bold');doc.setFontSize(11);doc.setTextColor(80,80,80)
    center('RECEIPT',y,11);y+=6;doc.setFontSize(12);doc.setTextColor(11,31,58)
    center(order.order_code,y,12);y+=5;doc.setFont('helvetica','normal');doc.setFontSize(9);doc.setTextColor(120,120,120)
    center(new Date(order.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}),y,9);y+=10
    ln(y);y+=8;doc.setFont('helvetica','bold');doc.setFontSize(9);doc.setTextColor(80,80,80)
    doc.text('PRODUCT',20,y);y+=6;doc.setFont('helvetica','normal');doc.setFontSize(10);doc.setTextColor(20,20,20)
    doc.text(order.product_name||order.product_code||'Product',20,y);y+=5
    const ut=Number(order.unit_price||0)*Number(order.quantity||1)
    row('Qty: '+order.quantity+' × '+Number(order.unit_price||0).toLocaleString(),ut.toLocaleString(),y);y+=5
    row('Delivery charge',Number(order.delivery_charge||0).toLocaleString(),y);y+=6;ln(y);y+=6
    doc.setFont('helvetica','bold');doc.setFontSize(11);doc.setTextColor(20,20,20)
    doc.text('Total',20,y);doc.setTextColor(11,31,58);doc.text(Number(order.total_amount||0).toLocaleString(),pw-20,y,{align:'right'});y+=10
    ln(y);y+=8;doc.setFontSize(8);doc.setTextColor(160,160,160)
    const d='This receipt is generated by '+brand.name+' on behalf of the seller. All matters are between buyer and seller. '+brand.name+' holds no liability.'
    const dl=doc.splitTextToSize(d,pw-40);doc.text(dl,20,y);y+=dl.length*4+5;ln(y);y+=6
    doc.setFontSize(8);doc.setTextColor(11,31,58);center('Powered by '+brand.name,y,8)
    doc.save('receipt-'+order.order_code+'.pdf');pdfGenerating=false
  }).catch(()=>{pdfGenerating=false})
}

// ── SVG icon component ───────────────────────────────────────────────────
const Ic = ({ n, size=16, color='currentColor' }) => {
  const s = { width:size, height:size, display:'inline-block', verticalAlign:'middle', flexShrink:0 }
  const p = { fill:'none', stroke:color, strokeWidth:1.8, strokeLinecap:'round', strokeLinejoin:'round' }
  if(n==='wa')   return <svg style={s} viewBox="0 0 24 24" fill={color} stroke="none"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
  if(n==='fb')   return <svg style={s} viewBox="0 0 24 24" fill={color} stroke="none"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
  if(n==='ph')   return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 014.5 10.5a19.79 19.79 0 01-3-8.63A2 2 0 013.48 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L7.91 7.91a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
  if(n==='pkg')  return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
  if(n==='ext')  return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
  if(n==='chk')  return <svg style={s} viewBox="0 0 24 24" {...p}><polyline points="20 6 9 17 4 12"/></svg>
  if(n==='hist') return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
  if(n==='store')return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
  if(n==='trk')  return <svg style={s} viewBox="0 0 24 24" {...p}><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
  if(n==='shld') return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
  if(n==='rcpt') return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
  if(n==='lock') return <svg style={s} viewBox="0 0 24 24" {...p}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
  if(n==='copy') return <svg style={s} viewBox="0 0 24 24" {...p}><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
  if(n==='pin')  return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
  if(n==='star') return <svg style={s} viewBox="0 0 24 24" {...p}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
  if(n==='bkm')  return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/></svg>
  if(n==='sun')  return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
  if(n==='moon') return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
  if(n==='usr')  return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  if(n==='note') return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
  if(n==='cal')  return <svg style={s} viewBox="0 0 24 24" {...p}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
  if(n==='analytics') return <svg style={s} viewBox="0 0 24 24" {...p}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
  return null
}

function AdBanner({ slot }) {
  if (!slot?.is_active || !slot?.image_url) return null
  return (
    <a href={slot.link_url||'#'} target="_blank" rel="noopener noreferrer"
      style={{ display:'block', position:'relative', marginBottom:'16px', borderRadius:'12px', overflow:'hidden', border:'1px solid '+BORDER }}>
      <img src={slot.image_url} alt="Ad" style={{ width:'100%', objectFit:'cover', maxHeight:'72px', display:'block' }} />
      <span style={{ position:'absolute', top:'6px', right:'8px', background:'rgba(0,0,0,0.5)', color:'#fff', padding:'2px 6px', borderRadius:'4px', fontSize:'9px' }}>Ad</span>
    </a>
  )
}

// ── Delivery countdown ───────────────────────────────────────────────────
function DeliveryCountdown({ date }) {
  if (!date) return null
  const today  = new Date(); today.setHours(0,0,0,0)
  const target = new Date(date); target.setHours(0,0,0,0)
  const diff   = Math.round((target - today) / (1000*60*60*24))
  if (diff < -1) return <span style={{ fontSize:'11.5px', fontWeight:700, color:RED, background:'#FEF2F2', padding:'3px 8px', borderRadius:'20px', display:'inline-flex', alignItems:'center', gap:'4px' }}><Ic n="cal" size={11} color={RED} />Overdue — contact seller</span>
  if (diff === 0) return <span style={{ fontSize:'11.5px', fontWeight:700, color:TEAL_D, background:'rgba(45,212,176,0.1)', padding:'3px 8px', borderRadius:'20px', display:'inline-flex', alignItems:'center', gap:'4px' }}><Ic n="cal" size={11} color={TEAL_D} />Expected today</span>
  if (diff === 1) return <span style={{ fontSize:'11.5px', fontWeight:700, color:GREEN, background:'#F0FDF4', padding:'3px 8px', borderRadius:'20px', display:'inline-flex', alignItems:'center', gap:'4px' }}><Ic n="cal" size={11} color={GREEN} />Expected tomorrow</span>
  return <span style={{ fontSize:'11.5px', fontWeight:600, color:MUTED, background:BG, padding:'3px 8px', borderRadius:'20px', display:'inline-flex', alignItems:'center', gap:'4px' }}><Ic n="cal" size={11} color={MUTED} />In {diff} days</span>
}

// ── Locked feature teaser ────────────────────────────────────────────────
function LockedFeature({ label, onUnlock }) {
  return (
    <button onClick={onUnlock}
      style={{ display:'flex', alignItems:'center', gap:'7px', padding:'9px 14px', borderRadius:'10px', border:'1.5px dashed '+BORDER, background:'rgba(11,31,58,0.02)', width:'100%', cursor:'pointer', fontFamily:'Space Grotesk, sans-serif', justifyContent:'space-between' }}>
      <span style={{ display:'flex', alignItems:'center', gap:'7px', fontSize:'12.5px', fontWeight:600, color:MUTED }}>
        <Ic n="lock" size={13} color={MUTED} />{label}
      </span>
      <span style={{ fontSize:'11.5px', fontWeight:700, color:NAVY, background:'rgba(11,31,58,0.06)', padding:'3px 10px', borderRadius:'20px' }}>Sign in →</span>
    </button>
  )
}

// ── PDF unlock modal ─────────────────────────────────────────────────────
function PdfUnlockModal({ onClose, navigate }) {
  return (
    <div onClick={e => e.target===e.currentTarget && onClose()} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
      <div style={{ background:'#fff', borderRadius:'20px', padding:'32px', maxWidth:'380px', width:'100%', textAlign:'center', boxShadow:'0 8px 40px rgba(0,0,0,0.15)' }}>
        <div style={{ width:'56px', height:'56px', borderRadius:'50%', background:'rgba(45,212,176,0.1)', border:'2px solid rgba(45,212,176,0.3)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
          <Ic n="rcpt" size={24} color={TEAL} />
        </div>
        <div style={{ fontSize:'18px', fontWeight:800, color:NAVY, marginBottom:'8px' }}>Download PDF receipts</div>
        <div style={{ fontSize:'13px', color:MUTED, marginBottom:'20px', lineHeight:1.7 }}>
          Create your free customer account to download PDF receipts, view your full order history and track spending across all sellers.
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'16px' }}>
          {['PDF receipts for every order','Full order history across all sellers','Spending analytics','Save your favourite sellers'].map((f,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px', textAlign:'left' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              <span style={{ fontSize:'12.5px', color:'#374151' }}>{f}</span>
            </div>
          ))}
        </div>
        <button onClick={() => navigate('/customer/signup')}
          style={{ width:'100%', padding:'13px', borderRadius:'12px', background:NAVY, border:'none', fontSize:'14px', fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'inherit', marginBottom:'8px' }}>
          Create free account →
        </button>
        <button onClick={() => navigate('/customer/login')}
          style={{ width:'100%', padding:'11px', borderRadius:'12px', border:'1px solid '+BORDER, background:'#fff', fontSize:'13px', fontWeight:600, color:MUTED, cursor:'pointer', fontFamily:'inherit', marginBottom:'12px' }}>
          Sign in instead
        </button>
        <button onClick={onClose} style={{ fontSize:'12px', color:MUTED, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>Maybe later</button>
      </div>
    </div>
  )
}

// ── Receipt modal ────────────────────────────────────────────────────────
function Receipt({ order, seller, brand, onClose, isLoggedIn, navigate }) {
  const balance      = Number(order.total_amount||0) - Number(order.amount_paid||0)
  const shopUsername = seller?.facebook_username || seller?.facebook_url?.split('/').filter(Boolean).pop() || ''
  const [showPdfModal, setShowPdfModal] = useState(false)
  const F = { fontFamily:'Space Grotesk, system-ui, sans-serif' }

  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'flex-end', justifyContent:'center', backdropFilter:'blur(4px)' }}>
      <div style={{ background:'#fff', width:'100%', maxWidth:'440px', borderRadius:'20px 20px 0 0', maxHeight:'92vh', display:'flex', flexDirection:'column', boxShadow:'0 -8px 40px rgba(0,0,0,0.2)' }}>
        {/* Header */}
        <div style={{ background:NAVY, padding:'14px 18px', borderRadius:'20px 20px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div style={{ ...F, fontSize:'14px', fontWeight:700, color:'#fff' }}>E-Receipt</div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={() => isLoggedIn ? generateReceiptPDF(order,seller,brand) : setShowPdfModal(true)}
              style={{ ...F, background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', fontSize:'12px', fontWeight:600, padding:'7px 14px', borderRadius:'8px', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px' }}>
              {!isLoggedIn && <Ic n="lock" size={11} color="rgba(255,255,255,0.7)" />}
              Download PDF
            </button>
            <button onClick={onClose}
              style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', fontSize:'18px', fontWeight:700, width:'32px', height:'32px', borderRadius:'8px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
          </div>
        </div>

        <div style={{ overflowY:'auto', flex:1, padding:'20px' }}>
          {/* Brand */}
          <div style={{ textAlign:'center', borderBottom:'1px solid '+BORDER, paddingBottom:'14px', marginBottom:'14px' }}>
            {brand.logo_url ? <img src={brand.logo_url} alt={brand.name} style={{ height:'26px', objectFit:'contain', margin:'0 auto 4px', display:'block' }} /> : <div style={{ ...F, fontSize:'15px', fontWeight:800, color:NAVY, marginBottom:'2px' }}>{brand.name}</div>}
            <div style={{ ...F, fontSize:'11px', color:MUTED }}>{brand.website}</div>
          </div>
          {/* Seller */}
          <div style={{ textAlign:'center', borderBottom:'1px solid '+BORDER, paddingBottom:'14px', marginBottom:'14px' }}>
            {seller?.logo_url
              ? <img src={seller.logo_url+'?t=1'} alt="logo" style={{ width:'48px', height:'48px', borderRadius:'12px', objectFit:'cover', margin:'0 auto 8px', display:'block', border:'1px solid '+BORDER }} />
              : <div style={{ width:'48px', height:'48px', borderRadius:'12px', background:'rgba(11,31,58,0.05)', margin:'0 auto 8px', display:'flex', alignItems:'center', justifyContent:'center' }}><Ic n="store" size={22} color={NAVY} /></div>
            }
            <div style={{ ...F, fontSize:'14px', fontWeight:700, color:NAVY }}>{seller?.shop_name}</div>
            {seller?.verification_status==='verified' && (
              <div style={{ ...F, fontSize:'11px', color:TEAL_D, marginTop:'2px', display:'flex', alignItems:'center', justifyContent:'center', gap:'3px' }}>
                <Ic n="chk" size={10} color={TEAL_D} />Verified seller
              </div>
            )}
            {seller?.phone && <div style={{ ...F, fontSize:'11px', color:MUTED, marginTop:'3px' }}>{seller.phone}</div>}
            {shopUsername && <div style={{ ...F, fontSize:'11px', color:TEAL_D, marginTop:'2px' }}>facebook.com/{shopUsername}</div>}
          </div>
          {/* Order code */}
          <div style={{ textAlign:'center', marginBottom:'14px' }}>
            <div style={{ ...F, fontSize:'10px', fontWeight:700, color:MUTED, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:'5px' }}>Receipt</div>
            <div style={{ ...F, fontSize:'15px', fontWeight:800, color:NAVY, fontFamily:'monospace' }}>{order.order_code}</div>
            <div style={{ ...F, fontSize:'11px', color:MUTED, marginTop:'2px' }}>{new Date(order.created_at).toLocaleDateString('en-BD',{day:'numeric',month:'long',year:'numeric'})}</div>
          </div>
          {/* Product */}
          <div style={{ borderTop:'1px solid '+BORDER, paddingTop:'12px', marginBottom:'12px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'6px' }}>
              <div style={{ flex:1, marginRight:'10px' }}>
                <div style={{ ...F, fontSize:'13px', fontWeight:700, color:NAVY }}>{order.product_name||order.product_code}</div>
                {order.variant_name && <div style={{ ...F, fontSize:'11px', color:MUTED, marginTop:'2px' }}>{order.variant_name}</div>}
                <div style={{ ...F, fontSize:'11px', color:MUTED, marginTop:'2px' }}>{order.quantity} × {Number(order.unit_price||0).toLocaleString()}</div>
              </div>
              <div style={{ ...F, fontSize:'13px', fontWeight:700, color:NAVY }}>{(Number(order.unit_price||0)*Number(order.quantity||1)).toLocaleString()}</div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', color:MUTED, marginBottom:'8px' }}>
              <span style={F}>Delivery</span><span style={F}>{Number(order.delivery_charge||0).toLocaleString()}</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', borderTop:'1px solid '+BORDER, paddingTop:'8px' }}>
              <span style={{ ...F, fontSize:'13px', fontWeight:700, color:NAVY }}>Total</span>
              <span style={{ ...F, fontSize:'14px', fontWeight:800, color:TEAL_D }}>{Number(order.total_amount||0).toLocaleString()}</span>
            </div>
          </div>
          {/* Payment */}
          <div style={{ background:BG, borderRadius:'10px', padding:'12px 14px', marginBottom:'12px' }}>
            <div style={{ ...F, fontSize:'11px', fontWeight:700, color:MUTED, marginBottom:'7px', textTransform:'uppercase', letterSpacing:'0.06em' }}>Payment</div>
            {[['Method',order.payment_method||'N/A'],['Status',order.payment_status||'N/A']].map(([l,v])=>(
              <div key={l} style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', marginBottom:'4px' }}>
                <span style={{ ...F, color:MUTED }}>{l}</span><span style={{ ...F, fontWeight:600, color:NAVY }}>{v}</span>
              </div>
            ))}
            {Number(order.amount_paid||0)>0 && <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', marginBottom:'4px' }}><span style={{ ...F, color:GREEN }}>Paid</span><span style={{ ...F, fontWeight:700, color:GREEN }}>{Number(order.amount_paid).toLocaleString()}</span></div>}
            {balance>0 && <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', borderTop:'1px solid '+BORDER, paddingTop:'7px', marginTop:'4px' }}><span style={{ ...F, color:AMBER, fontWeight:600 }}>Balance due</span><span style={{ ...F, fontWeight:800, color:AMBER }}>{balance.toLocaleString()}</span></div>}
          </div>
          {/* Footer */}
          <div style={{ textAlign:'center', borderTop:'1px solid '+BORDER, paddingTop:'12px', marginBottom:'12px' }}>
            <div style={{ ...F, fontSize:'13px', fontWeight:700, color:NAVY, marginBottom:'3px' }}>Thank you for shopping!</div>
            <div style={{ ...F, fontSize:'11px', color:MUTED }}>with {seller?.shop_name}</div>
          </div>
          <div style={{ background:BG, borderRadius:'10px', padding:'10px 12px', marginBottom:'12px' }}>
            <div style={{ ...F, fontSize:'11px', color:MUTED, lineHeight:1.6 }}>This receipt is generated by {brand.name} on behalf of the seller. All matters are solely between buyer and seller. {brand.name} holds no liability.</div>
          </div>
          <div style={{ textAlign:'center', paddingBottom:'8px' }}>
            <div style={{ ...F, fontSize:'11px', color:MUTED, marginBottom:'4px' }}>Powered by</div>
            <div style={{ ...F, fontSize:'13px', fontWeight:800, color:NAVY }}>{brand.name}</div>
          </div>
          {/* Unlock prompt for unregistered */}
          {!isLoggedIn && (
            <div style={{ background:'linear-gradient(135deg,rgba(11,31,58,0.04),rgba(45,212,176,0.06))', border:'1px solid rgba(45,212,176,0.2)', borderRadius:'12px', padding:'14px 16px', marginTop:'8px' }}>
              <div style={{ ...F, fontSize:'12.5px', fontWeight:700, color:NAVY, marginBottom:'6px' }}>Save this receipt forever</div>
              <div style={{ ...F, fontSize:'12px', color:MUTED, marginBottom:'10px', lineHeight:1.6 }}>Create a free account to download PDFs and access all your receipts anytime.</div>
              <button onClick={() => { onClose(); navigate('/customer/signup') }}
                style={{ ...F, width:'100%', padding:'10px', borderRadius:'10px', background:NAVY, border:'none', fontSize:'13px', fontWeight:700, color:'#fff', cursor:'pointer' }}>
                Create free account
              </button>
            </div>
          )}
        </div>
      </div>
      {showPdfModal && <PdfUnlockModal onClose={() => setShowPdfModal(false)} navigate={navigate} />}
    </div>
  )
}

// ── Main component ───────────────────────────────────────────────────────
function TrackOrder() {
  const { brand }    = useBrand()
  const navigate     = useNavigate()
  const F = { fontFamily:'Space Grotesk, system-ui, sans-serif' }

  const [countryCode, setCountryCode]   = useState(COUNTRY_CODES[0])
  const [phone, setPhone]               = useState('')
  const [sellerGroups, setSellerGroups] = useState([])
  const [loading, setLoading]           = useState(false)
  const [searched, setSearched]         = useState(false)
  const [error, setError]               = useState('')
  const [adSlots, setAdSlots]           = useState({})
  const [receipt, setReceipt]           = useState(null)
  const [sellerCount, setSellerCount]   = useState(null)
  const [isLoggedIn, setIsLoggedIn]     = useState(false)
  const [searchHistory, setSearchHistory] = useState([])
  const [bookmarks, setBookmarks]       = useState(new Set())
  const [copiedPhone, setCopiedPhone]   = useState(null)
  const [showPdfModal, setShowPdfModal] = useState(false)

  useEffect(() => {
    fetchAdSlots()
    fetchSellerCount()
    checkAuth()
    loadSearchHistory()
    loadBookmarks()
  }, [])

  const fetchAdSlots = async () => {
    const { data } = await supabase.from('ad_slots').select('*').eq('is_active',true)
    const slots = {}; data?.forEach(s=>{ slots[s.slot_type]=s }); setAdSlots(slots)
  }

  const fetchSellerCount = async () => {
    const { count } = await supabase.from('sellers').select('*',{count:'exact',head:true}).eq('verification_status','verified')
    setSellerCount(count || null)
  }

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    const { data: acc } = await supabase.from('customer_accounts').select('id').eq('user_id', user.id).single()
    setIsLoggedIn(!!acc)
  }

  const loadSearchHistory = () => {
    try { setSearchHistory(JSON.parse(localStorage.getItem('pc_track_history') || '[]')) } catch {}
  }

  const saveSearchHistory = (num) => {
    try {
      const hist = JSON.parse(localStorage.getItem('pc_track_history') || '[]')
      const updated = [num, ...hist.filter(h => h !== num)].slice(0, 3)
      localStorage.setItem('pc_track_history', JSON.stringify(updated))
      setSearchHistory(updated)
    } catch {}
  }

  const loadBookmarks = () => {
    try { setBookmarks(new Set(JSON.parse(localStorage.getItem('pc_track_bookmarks') || '[]'))) } catch {}
  }

  const toggleBookmark = (orderId) => {
    const next = new Set(bookmarks)
    if (next.has(orderId)) next.delete(orderId); else next.add(orderId)
    setBookmarks(next)
    try { localStorage.setItem('pc_track_bookmarks', JSON.stringify([...next])) } catch {}
  }

  const buildFullNumber = () => {
    const digits = phone.replace(/\D/g,'').replace(/^0+/,'')
    return countryCode.code === '+other' ? digits : countryCode.code + digits
  }

  const handleTrack = async (overridePhone) => {
    const usePhone = overridePhone || phone
    const digits = usePhone.replace(/\D/g,'').replace(/^0+/,'')
    if (!digits || digits.length < 6) { setError('Please enter a valid phone number'); return }
    setLoading(true); setError(''); setSearched(false); setSellerGroups([])
    const full  = buildFullNumber()
    const local0 = full.startsWith('+880') ? '0'+full.slice(4) : null
    const variants = [...new Set([full, usePhone.trim(), digits, ...(local0 ? [local0] : [])])]
    let customers = []
    for (const v of variants) {
      const { data } = await supabase.from('customers').select('id,seller_id').eq('phone',v)
      if (data?.length) customers = [...customers,...data]
    }
    const uniqueCustomers = customers.filter((c,i,a)=>a.findIndex(x=>x.id===c.id)===i)
    if (!uniqueCustomers.length) { setSearched(true); setLoading(false); saveSearchHistory(usePhone); return }
    const customerIds = uniqueCustomers.map(c=>c.id)
    const sellerIds   = [...new Set(uniqueCustomers.map(c=>c.seller_id))]
    const { data:ordersData } = await supabase.from('orders')
      .select('*, customers(name,phone)').in('customer_id',customerIds)
      .not('status','in','("closed","return")').order('created_at',{ascending:false})
    const { data:sellersData } = await supabase.from('sellers')
      .select('id,shop_name,facebook_url,facebook_username,phone,logo_url,district,country,verification_status,tagline').in('id',sellerIds)
    const groups = sellersData?.map(seller=>({
      seller,
      orders:(ordersData||[]).filter(o=>{const c=uniqueCustomers.find(cu=>cu.id===o.customer_id);return c?.seller_id===seller.id})
    })).filter(g=>g.orders.length>0)||[]
    setSellerGroups(groups); setSearched(true); setLoading(false)
    saveSearchHistory(usePhone)
  }

  const getShopUsername = s => s?.facebook_username||s?.facebook_url?.split('/').filter(Boolean).pop()||''
  const totalOrders = sellerGroups.reduce((s,g)=>s+g.orders.length,0)

  const handleCopyPhone = (ph) => {
    navigator.clipboard.writeText(ph)
    setCopiedPhone(ph); setTimeout(() => setCopiedPhone(null), 2000)
  }

  const getGreetingIcon = () => {
    const h = new Date().getHours()
    return h >= 21 || h < 6 ? 'moon' : 'sun'
  }
  const getGreetingText = () => {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 17) return 'Good afternoon'
    if (h < 21) return 'Good evening'
    return 'Good night'
  }

  return (
    <div style={{ minHeight:'100vh', background:BG, ...F }}>
      <style>{`
        @keyframes to-fade{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
        @keyframes to-spin{to{transform:rotate(360deg)}}
        .to-anim{animation:to-fade 0.35s ease forwards}
        .to-cnt{display:flex;align-items:center;justify-content:center;gap:7px;flex:1;padding:10px;border-radius:10px;font-size:12px;font-weight:600;cursor:pointer;border:none;transition:opacity 0.15s;text-decoration:none;font-family:Space Grotesk,system-ui,sans-serif}
        .to-cnt:hover{opacity:0.82}
        .to-card{background:#fff;border-radius:14px;padding:16px;margin-bottom:10px;border:1px solid ${BORDER};transition:box-shadow 0.2s}
        .to-card:hover{box-shadow:0 4px 20px rgba(11,31,58,0.07)}
        .to-side-card{background:#fff;border-radius:14px;padding:20px;border:1px solid ${BORDER};margin-bottom:14px}
        @media(max-width:768px){.to-layout{flex-direction:column!important}.to-sidebar{display:none!important}.to-hero-inner{flex-direction:column!important;gap:16px!important}}
      `}</style>

      {/* ── NAV ── */}
      <nav style={{ background:'#fff', borderBottom:'1px solid '+BORDER, padding:'0 24px', height:'56px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100, boxShadow:'0 1px 4px rgba(11,31,58,0.06)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          {brand.logo_url
            ? <img src={brand.logo_url} alt={brand.name} style={{ height:'32px', objectFit:'contain' }} />
            : <div style={{ fontSize:'18px', fontWeight:800, color:NAVY }}>{brand.name}</div>
          }
          <div style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'12px', color:MUTED }}>
            <Ic n={getGreetingIcon()} size={13} color={MUTED} />
            {getGreetingText()}
          </div>
        </div>
        <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
          <Link to="/sellers" style={{ fontSize:'12.5px', fontWeight:600, color:MUTED, textDecoration:'none', padding:'6px 12px', borderRadius:'8px', border:'1px solid '+BORDER, background:'#fff' }}>Seller directory</Link>
          {isLoggedIn
            ? <Link to="/customer/portal" style={{ fontSize:'12.5px', fontWeight:700, color:'#fff', textDecoration:'none', padding:'6px 14px', borderRadius:'8px', background:NAVY, display:'flex', alignItems:'center', gap:'5px' }}><Ic n="usr" size={12} color="#fff" />My account</Link>
            : <Link to="/customer/login" style={{ fontSize:'12.5px', fontWeight:700, color:'#fff', textDecoration:'none', padding:'6px 14px', borderRadius:'8px', background:NAVY }}>Sign in</Link>
          }
        </div>
      </nav>

      {/* ── HERO ── */}
      <div style={{ background:'linear-gradient(160deg,'+NAVY+' 0%,#0D2B4E 60%,#0A2240 100%)', padding:'40px 24px 48px' }}>
        <div style={{ maxWidth:'1100px', margin:'0 auto' }}>
          <div className="to-hero-inner" style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', gap:'32px' }}>
            <div style={{ flex:1 }}>
              <div style={{ display:'inline-flex', alignItems:'center', gap:'7px', background:'rgba(45,212,176,0.12)', border:'1px solid rgba(45,212,176,0.25)', borderRadius:'20px', padding:'4px 14px', marginBottom:'14px' }}>
                <Ic n="trk" size={13} color={TEAL} />
                <span style={{ fontSize:'11.5px', color:TEAL, fontWeight:700, letterSpacing:'0.04em' }}>REAL-TIME ORDER TRACKING</span>
              </div>
              <div style={{ fontSize:'28px', fontWeight:800, color:'#fff', lineHeight:1.15, letterSpacing:'-0.5px', marginBottom:'8px' }}>
                Track your orders<br />from any seller.
              </div>
              <div style={{ fontSize:'13.5px', color:'rgba(255,255,255,0.55)', lineHeight:1.7, maxWidth:'460px' }}>
                Enter the phone number you used when placing your order to see all your active deliveries in one place.
              </div>
            </div>
            <div style={{ display:'flex', gap:'24px', flexShrink:0 }}>
              {[
                [sellerCount ? sellerCount+'+' : '500+', 'Verified sellers'],
                ['50K+', 'Orders tracked'],
                ['4.9', 'Avg rating'],
              ].map(([n,l])=>(
                <div key={l} style={{ textAlign:'center' }}>
                  <div style={{ fontSize:'22px', fontWeight:800, color:'#fff', letterSpacing:'-0.5px', lineHeight:1 }}>{n}</div>
                  <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.45)', marginTop:'3px' }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── SEARCH BAR ── */}
      <div style={{ background:'#fff', borderBottom:'1px solid '+BORDER, padding:'20px 24px' }}>
        <div style={{ maxWidth:'1100px', margin:'0 auto' }}>
          <div style={{ display:'flex', gap:'10px', alignItems:'stretch', flexWrap:'wrap' }}>
            {/* Country selector */}
            <div style={{ position:'relative', flexShrink:0 }}>
              <select value={countryCode.code}
                onChange={e => setCountryCode(COUNTRY_CODES.find(c=>c.code===e.target.value)||COUNTRY_CODES[0])}
                style={{ height:'48px', padding:'0 32px 0 12px', border:'1.5px solid '+BORDER, borderRadius:'10px', fontSize:'13px', fontWeight:600, color:NAVY, background:'#fff', cursor:'pointer', fontFamily:'Space Grotesk, system-ui, sans-serif', outline:'none', appearance:'none', minWidth:'170px' }}
                onFocus={e=>e.target.style.borderColor=TEAL} onBlur={e=>e.target.style.borderColor=BORDER}>
                {COUNTRY_CODES.map(c=>(
                  <option key={c.code} value={c.code}>{c.flag} {c.name} ({c.code})</option>
                ))}
              </select>
              <div style={{ position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={MUTED} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>
            {/* Phone */}
            <input type="tel" value={phone} onChange={e=>setPhone(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleTrack()}
              placeholder={countryCode.hint || 'Your phone number'}
              style={{ flex:1, minWidth:'200px', border:'1.5px solid '+BORDER, borderRadius:'10px', padding:'0 16px', fontSize:'14px', color:NAVY, outline:'none', fontFamily:'Space Grotesk, system-ui, sans-serif', height:'48px', boxSizing:'border-box' }}
              onFocus={e=>e.target.style.borderColor=TEAL} onBlur={e=>e.target.style.borderColor=BORDER} />
            {/* Search button */}
            <button onClick={() => handleTrack()} disabled={loading}
              style={{ background:loading ? MUTED : NAVY, color:'#fff', border:'none', borderRadius:'10px', padding:'0 24px', fontSize:'14px', fontWeight:700, cursor:loading?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:'8px', fontFamily:'Space Grotesk, system-ui, sans-serif', height:'48px', flexShrink:0 }}>
              {loading
                ? <><div style={{ width:'16px', height:'16px', borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'to-spin 0.7s linear infinite' }} />Searching...</>
                : <><Ic n="trk" size={15} color="#fff" />Track order</>
              }
            </button>
          </div>
          {error && <div style={{ fontSize:'12px', color:RED, marginTop:'8px' }}>{error}</div>}

          {/* Tip */}
          <div style={{ fontSize:'12px', color:MUTED, marginTop:'10px', display:'flex', alignItems:'center', gap:'5px' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={AMBER} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span><strong style={{ color:AMBER }}>Tip:</strong> Enter your number without the leading zero. Example: <span style={{ fontFamily:'monospace', color:NAVY, fontWeight:600 }}>{countryCode.code}1712345678</span></span>
          </div>

          {/* Search history */}
          {searchHistory.length > 0 && (
            <div style={{ marginTop:'12px', display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
              <span style={{ fontSize:'11.5px', color:MUTED }}>Recent:</span>
              {searchHistory.map(h => (
                <button key={h} onClick={() => { setPhone(h); handleTrack(h) }}
                  style={{ fontSize:'12px', fontWeight:600, color:NAVY, background:'rgba(11,31,58,0.06)', border:'none', borderRadius:'20px', padding:'4px 12px', cursor:'pointer', fontFamily:'inherit' }}>
                  {h}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── MAIN LAYOUT ── */}
      <div style={{ maxWidth:'1100px', margin:'0 auto', padding:'24px' }}>
        <div className="to-layout" style={{ display:'flex', gap:'24px', alignItems:'flex-start' }}>

          {/* LEFT — Results */}
          <div style={{ flex:1, minWidth:0 }}>
            <AdBanner slot={adSlots['top_banner']} />

            {/* Register nudge — shown always to unregistered */}
            {!isLoggedIn && (
              <div style={{ background:'linear-gradient(135deg,rgba(11,31,58,0.03),rgba(45,212,176,0.06))', border:'1px solid rgba(45,212,176,0.2)', borderRadius:'14px', padding:'14px 16px', marginBottom:'16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px' }}>
                <div>
                  <div style={{ fontSize:'13px', fontWeight:700, color:NAVY, marginBottom:'3px' }}>Get more from your orders</div>
                  <div style={{ fontSize:'12px', color:MUTED, lineHeight:1.5 }}>Sign in to unlock PDF receipts, full order history and spending analytics — free.</div>
                </div>
                <div style={{ display:'flex', gap:'7px', flexShrink:0 }}>
                  <Link to="/customer/login" style={{ fontSize:'12px', fontWeight:600, color:NAVY, textDecoration:'none', padding:'7px 12px', borderRadius:'8px', border:'1px solid '+BORDER, background:'#fff', whiteSpace:'nowrap' }}>Sign in</Link>
                  <Link to="/customer/signup" style={{ fontSize:'12px', fontWeight:700, color:'#fff', textDecoration:'none', padding:'7px 12px', borderRadius:'8px', background:NAVY, whiteSpace:'nowrap' }}>Free account</Link>
                </div>
              </div>
            )}

            {searched && (
              <div className="to-anim">
                {totalOrders === 0 ? (
                  <div className="to-card" style={{ textAlign:'center', padding:'36px 24px', marginBottom:'20px' }}>
                    <div style={{ width:'52px', height:'52px', borderRadius:'50%', background:'rgba(11,31,58,0.05)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                      <Ic n="pkg" size={26} color={MUTED} />
                    </div>
                    <div style={{ fontSize:'16px', fontWeight:700, color:NAVY, marginBottom:'6px' }}>No active orders found</div>
                    <div style={{ fontSize:'13px', color:MUTED, marginBottom:'20px', lineHeight:1.6 }}>
                      No active orders for <strong>{countryCode.code} {phone}</strong>. Make sure the number matches exactly what you gave the seller.
                    </div>
                    <div style={{ display:'flex', gap:'10px', justifyContent:'center', flexWrap:'wrap' }}>
                      {isLoggedIn
                        ? <Link to="/customer/portal" style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:NAVY, color:'#fff', padding:'10px 20px', borderRadius:'10px', fontSize:'13px', fontWeight:700, textDecoration:'none' }}><Ic n="hist" size={14} color="#fff" />My portal</Link>
                        : <Link to="/customer/signup" style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:NAVY, color:'#fff', padding:'10px 20px', borderRadius:'10px', fontSize:'13px', fontWeight:700, textDecoration:'none' }}><Ic n="usr" size={14} color="#fff" />Create account</Link>
                      }
                      <Link to="/sellers" style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:BG, color:NAVY, padding:'10px 20px', borderRadius:'10px', fontSize:'13px', fontWeight:600, textDecoration:'none', border:'1px solid '+BORDER }}>
                        <Ic n="store" size={14} color={NAVY} />Find seller
                      </Link>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Results found nudge */}
                    {!isLoggedIn && (
                      <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:'12px', padding:'12px 16px', marginBottom:'14px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px' }}>
                        <div style={{ fontSize:'12.5px', color:'#166534' }}>
                          <strong>Found {totalOrders} order{totalOrders!==1?'s':''}.</strong> Create a free account to save these and never search again.
                        </div>
                        <Link to="/customer/signup" style={{ fontSize:'12px', fontWeight:700, color:'#fff', textDecoration:'none', padding:'6px 12px', borderRadius:'8px', background:GREEN, flexShrink:0, whiteSpace:'nowrap' }}>Save orders</Link>
                      </div>
                    )}

                    <div style={{ fontSize:'12px', color:MUTED, marginBottom:'14px', display:'flex', alignItems:'center', gap:'6px' }}>
                      <Ic n="pkg" size={13} color={TEAL_D} />
                      <span style={{ color:TEAL_D, fontWeight:700 }}>{totalOrders} active order{totalOrders!==1?'s':''}</span> found
                    </div>

                    {sellerGroups.map(({ seller, orders }) => (
                      <div key={seller.id} style={{ marginBottom:'24px' }}>
                        {/* Seller card */}
                        <div className="to-card">
                          <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'14px' }}>
                            {seller.logo_url
                              ? <img src={seller.logo_url+'?t=1'} alt="logo" style={{ width:'44px', height:'44px', borderRadius:'10px', objectFit:'cover', flexShrink:0, border:'1px solid '+BORDER }} />
                              : <div style={{ width:'44px', height:'44px', borderRadius:'10px', background:'rgba(11,31,58,0.05)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><Ic n="store" size={20} color={NAVY} /></div>
                            }
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', marginBottom:'2px' }}>
                                <div style={{ fontSize:'14px', fontWeight:700, color:NAVY }}>{seller.shop_name}</div>
                                {seller.verification_status==='verified' && (
                                  <span style={{ fontSize:'11px', color:TEAL_D, fontWeight:600, display:'flex', alignItems:'center', gap:'3px' }}>
                                    <Ic n="chk" size={10} color={TEAL_D} />Verified
                                  </span>
                                )}
                              </div>
                              {seller.tagline && <div style={{ fontSize:'12px', color:MUTED, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{seller.tagline}</div>}
                              {(seller.district||seller.country) && (
                                <div style={{ fontSize:'11px', color:MUTED, marginTop:'2px', display:'flex', alignItems:'center', gap:'4px' }}>
                                  <Ic n="pin" size={10} color={MUTED} />{seller.district||seller.country}
                                </div>
                              )}
                            </div>
                            {getShopUsername(seller) && (
                              <a href={'/web/'+getShopUsername(seller)} style={{ fontSize:'12px', color:TEAL_D, fontWeight:600, textDecoration:'none', flexShrink:0, display:'flex', alignItems:'center', gap:'4px' }}>
                                Shop <Ic n="ext" size={11} color={TEAL_D} />
                              </a>
                            )}
                          </div>
                          <div style={{ display:'flex', gap:'8px' }}>
                            {seller.phone && (
                              <a href={'https://wa.me/'+seller.phone.replace(/\D/g,'')} target="_blank" rel="noopener noreferrer" className="to-cnt" style={{ background:'#F0FDF4', color:'#166534' }}>
                                <Ic n="wa" size={14} color={GREEN} />WhatsApp
                              </a>
                            )}
                            {seller.facebook_url && (
                              <a href={seller.facebook_url} target="_blank" rel="noopener noreferrer" className="to-cnt" style={{ background:'#EFF6FF', color:'#1D4ED8' }}>
                                <Ic n="fb" size={14} color="#1877F2" />Facebook
                              </a>
                            )}
                            {seller.phone && (
                              <button onClick={() => handleCopyPhone(seller.phone)} className="to-cnt" style={{ background:copiedPhone===seller.phone?'#F0FDF4':BG, color:copiedPhone===seller.phone?GREEN:MUTED, flex:'0 0 auto', padding:'10px 14px', border:'none' }}>
                                <Ic n={copiedPhone===seller.phone?'chk':'copy'} size={14} color={copiedPhone===seller.phone?GREEN:MUTED} />
                                {copiedPhone===seller.phone ? 'Copied!' : 'Copy no.'}
                              </button>
                            )}
                          </div>
                        </div>

                        {orders.map(order => {
                          const isManual  = MANUAL_COURIERS.includes(order.courier)
                          const isPickup  = PICKUP_COURIERS.includes(order.courier)
                          const balance   = Number(order.total_amount||0)-Number(order.amount_paid||0)
                          const currentIdx = STATUS_STEPS.indexOf(order.status)
                          const hasTracking = !isManual&&!isPickup&&(order.tracking_url||order.tracking_number)
                          const sc = STATUS_COLORS[order.status]||{ bg:BG, color:MUTED }
                          const isBookmarked = bookmarks.has(order.id)

                          return (
                            <div key={order.id} style={{ background:'#fff', borderRadius:'14px', padding:'16px', marginBottom:'10px', border:'1px solid '+BORDER, borderLeft:'3px solid '+TEAL }}>
                              {/* Order header */}
                              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'12px' }}>
                                <div>
                                  <div style={{ fontSize:'14px', fontWeight:800, color:TEAL_D, fontFamily:'monospace' }}>{order.order_code}</div>
                                  <div style={{ fontSize:'11px', color:MUTED, marginTop:'2px' }}>{new Date(order.created_at).toLocaleDateString('en-BD',{day:'numeric',month:'short',year:'numeric'})}</div>
                                </div>
                                <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                                  <span style={{ background:sc.bg, color:sc.color, fontSize:'11px', fontWeight:700, padding:'4px 10px', borderRadius:'20px' }}>{STATUS_LABELS[order.status]||order.status}</span>
                                  <button onClick={() => isLoggedIn ? toggleBookmark(order.id) : setShowPdfModal(true)}
                                    title={isLoggedIn ? (isBookmarked ? 'Remove bookmark' : 'Bookmark order') : 'Sign in to bookmark'}
                                    style={{ background:'none', border:'none', cursor:'pointer', padding:'4px', display:'flex', alignItems:'center' }}>
                                    <Ic n="bkm" size={16} color={isBookmarked ? TEAL_D : BORDER} />
                                  </button>
                                </div>
                              </div>

                              {/* Product */}
                              <div style={{ background:BG, borderRadius:'10px', padding:'12px 14px', marginBottom:'12px' }}>
                                <div style={{ fontSize:'13px', fontWeight:700, color:NAVY, marginBottom:'4px' }}>{order.product_name||order.product_code}</div>
                                {order.variant_name && <div style={{ fontSize:'11px', color:MUTED, marginBottom:'4px' }}>{order.variant_name}</div>}
                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                  <div style={{ fontSize:'12px', color:MUTED }}>Qty: {order.quantity}</div>
                                  <div style={{ fontSize:'14px', fontWeight:800, color:NAVY }}>{Number(order.total_amount||0).toLocaleString()}</div>
                                </div>
                                {balance>0 && <div style={{ fontSize:'12px', color:AMBER, fontWeight:600, marginTop:'4px' }}>Balance due: {balance.toLocaleString()}</div>}
                              </div>

                              {/* Seller note */}
                              {order.notes && (
                                <div style={{ background:'rgba(45,212,176,0.06)', border:'1px solid rgba(45,212,176,0.2)', borderRadius:'10px', padding:'10px 12px', marginBottom:'12px', display:'flex', alignItems:'flex-start', gap:'8px' }}>
                                  <Ic n="note" size={13} color={TEAL_D} />
                                  <div>
                                    <div style={{ fontSize:'11px', fontWeight:700, color:TEAL_D, marginBottom:'3px' }}>Seller note</div>
                                    <div style={{ fontSize:'12.5px', color:NAVY, lineHeight:1.5 }}>{order.notes}</div>
                                  </div>
                                </div>
                              )}

                              {/* Expected delivery countdown */}
                              {order.expected_delivery && (
                                <div style={{ marginBottom:'12px' }}>
                                  <DeliveryCountdown date={order.expected_delivery} />
                                </div>
                              )}

                              {/* Progress tracker */}
                              <div style={{ overflowX:'auto', paddingBottom:'4px', marginBottom:'12px' }}>
                                <div style={{ display:'flex', alignItems:'center', minWidth:'max-content' }}>
                                  {STATUS_STEPS.map((step,i,arr)=>{
                                    const done=i<currentIdx; const cur=step===order.status
                                    return (
                                      <div key={step} style={{ display:'flex', alignItems:'center' }}>
                                        <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                                          <div style={{ width:'20px', height:'20px', borderRadius:'50%', background:done?GREEN:cur?TEAL:'#E5E7EB', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, boxShadow:cur?'0 0 0 3px rgba(45,212,176,0.2)':'none', transition:'all 0.2s' }}>
                                            {done && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                                          </div>
                                          <div style={{ marginTop:'4px', fontSize:'9px', fontWeight:cur?700:400, color:cur?TEAL_D:done?GREEN:'#9CA3AF', whiteSpace:'nowrap' }}>{STATUS_LABELS[step]}</div>
                                        </div>
                                        {i<arr.length-1&&<div style={{ width:'24px', height:'2px', background:i<currentIdx?GREEN:BORDER, flexShrink:0, margin:'0 2px 14px' }}/>}
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>

                              {/* Tracking info */}
                              {hasTracking && (
                                <div style={{ background:'rgba(11,31,58,0.03)', borderRadius:'10px', padding:'12px 14px', marginBottom:'12px', border:'1px solid '+BORDER }}>
                                  <div style={{ fontSize:'11px', fontWeight:700, color:NAVY, marginBottom:'6px' }}>{order.courier} Tracking</div>
                                  {order.tracking_number && <div style={{ fontSize:'12px', fontFamily:'monospace', color:NAVY, background:'#fff', padding:'6px 10px', borderRadius:'7px', marginBottom:'8px', border:'1px solid '+BORDER }}>{order.tracking_number}</div>}
                                  {order.tracking_url && <a href={order.tracking_url} target="_blank" rel="noreferrer" style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:NAVY, color:'#fff', fontSize:'12px', fontWeight:600, padding:'7px 14px', borderRadius:'8px', textDecoration:'none' }}>Track on {order.courier} <Ic n="ext" size={12} color="#fff" /></a>}
                                </div>
                              )}
                              {!isManual&&!isPickup&&!hasTracking&&['with_courier','shipped'].includes(order.status)&&(
                                <div style={{ background:BG, borderRadius:'10px', padding:'10px 14px', marginBottom:'12px', fontSize:'12px', color:MUTED }}>Tracking not yet available — contact the seller for updates.</div>
                              )}
                              {isPickup && (
                                <div style={{ background:'#FFFBEB', borderRadius:'10px', padding:'10px 14px', marginBottom:'12px', border:'1px solid #FDE68A' }}>
                                  <div style={{ fontSize:'12px', fontWeight:700, color:AMBER }}>{order.courier}</div>
                                  <div style={{ fontSize:'11px', color:'#92400E', marginTop:'2px' }}>Contact the seller to arrange pickup.</div>
                                </div>
                              )}
                              {isManual&&order.delivery_person_name && (
                                <div style={{ background:'#F0FDF4', borderRadius:'10px', padding:'12px 14px', marginBottom:'12px', border:'1px solid #BBF7D0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                                  <div>
                                    <div style={{ fontSize:'11px', fontWeight:700, color:GREEN, marginBottom:'3px' }}>Delivery person</div>
                                    <div style={{ fontSize:'13px', fontWeight:600, color:NAVY }}>{order.delivery_person_name}</div>
                                    {order.delivery_person_phone && <div style={{ fontSize:'11px', color:MUTED }}>{order.delivery_person_phone}</div>}
                                  </div>
                                  {order.delivery_person_phone && <a href={'tel:'+order.delivery_person_phone} style={{ background:GREEN, color:'#fff', fontSize:'12px', fontWeight:700, padding:'8px 14px', borderRadius:'8px', textDecoration:'none' }}>Call</a>}
                                </div>
                              )}

                              {/* View receipt */}
                              <button onClick={()=>setReceipt({order,seller})}
                                style={{ width:'100%', background:BG, border:'1px solid '+BORDER, color:MUTED, fontSize:'12px', fontWeight:600, padding:'10px', borderRadius:'10px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px', fontFamily:'Space Grotesk, system-ui, sans-serif' }}>
                                <Ic n="rcpt" size={14} color={MUTED} />View e-receipt
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    ))}

                    {/* Locked features for unregistered */}
                    {!isLoggedIn && (
                      <div style={{ background:'#fff', borderRadius:'14px', border:'1px solid '+BORDER, padding:'16px', marginBottom:'20px' }}>
                        <div style={{ fontSize:'12.5px', fontWeight:700, color:NAVY, marginBottom:'12px', display:'flex', alignItems:'center', gap:'7px' }}>
                          <Ic n="lock" size={13} color={NAVY} />Unlock with a free account
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'14px' }}>
                          <LockedFeature label="Download PDF receipts" onUnlock={() => setShowPdfModal(true)} />
                          <LockedFeature label="Full order history — all past orders" onUnlock={() => navigate('/customer/signup')} />
                          <LockedFeature label="Spending analytics across all sellers" onUnlock={() => navigate('/customer/signup')} />
                          <LockedFeature label="Save favourite sellers" onUnlock={() => navigate('/customer/signup')} />
                        </div>
                        <Link to="/customer/signup" style={{ display:'block', background:NAVY, color:'#fff', textAlign:'center', padding:'12px', borderRadius:'10px', fontSize:'13px', fontWeight:700, textDecoration:'none' }}>
                          Create free account — takes 30 seconds
                        </Link>
                        <div style={{ textAlign:'center', fontSize:'11px', color:MUTED, marginTop:'6px' }}>Free forever · No credit card · No spam</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Quick links */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'20px' }}>
              {[
                isLoggedIn
                  ? { href:'/customer/portal', icon:'hist', label:'My portal', sub:'Orders & receipts' }
                  : { href:'/customer/signup', icon:'usr', label:'Create account', sub:'Free · Unlock all features' },
                { to:'/sellers', icon:'store', label:'Seller directory', sub:'Find verified sellers' },
              ].map((item,i) => {
                const El = item.to ? Link : 'a'
                const props = item.to ? { to:item.to } : { href:item.href }
                return (
                  <El key={i} {...props} style={{ background:'#fff', border:'1px solid '+BORDER, borderRadius:'14px', padding:'16px', textAlign:'center', textDecoration:'none', display:'block', transition:'border-color 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=TEAL}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=BORDER}>
                    <Ic n={item.icon} size={22} color={NAVY} />
                    <div style={{ fontSize:'13px', fontWeight:700, color:NAVY, marginTop:'7px' }}>{item.label}</div>
                    <div style={{ fontSize:'11px', color:MUTED, marginTop:'2px' }}>{item.sub}</div>
                  </El>
                )
              })}
            </div>

            <AdBanner slot={adSlots['mid_mrec']} />

            {/* Disclaimers */}
            <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'14px', padding:'16px', marginBottom:'12px' }}>
              <div style={{ fontSize:'12px', fontWeight:700, color:RED, marginBottom:'8px' }}>Important</div>
              {['All purchases and deliveries are solely between you and the seller. PageConnect is a technology platform only.','For product issues — contact the seller. For delivery issues — contact the courier.','PageConnect cannot intervene in any transaction or dispute.'].map((t,i)=>(
                <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:'8px', marginBottom:'5px' }}>
                  <span style={{ color:'#FCA5A5', flexShrink:0 }}>•</span>
                  <span style={{ fontSize:'12px', color:'#991B1B', lineHeight:1.6 }}>{t}</span>
                </div>
              ))}
            </div>
            <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'14px', padding:'14px', marginBottom:'20px' }}>
              <div style={{ fontSize:'12px', fontWeight:700, color:AMBER, marginBottom:'4px' }}>Stay safe</div>
              <div style={{ fontSize:'12px', color:'#92400E', lineHeight:1.6 }}>PageConnect will never ask for your OTP or password. Report scams to <a href={'mailto:'+(brand.email||'help@pageconnect.io')} style={{ fontWeight:700, color:AMBER }}>{brand.email||'help@pageconnect.io'}</a></div>
            </div>
            <AdBanner slot={adSlots['bottom_banner']} />
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="to-sidebar" style={{ width:'290px', flexShrink:0 }}>

            {/* Auth-aware sidebar CTA */}
            {isLoggedIn ? (
              <div style={{ background:NAVY, borderRadius:'16px', padding:'20px', marginBottom:'14px' }}>
                <div style={{ fontSize:'14px', fontWeight:800, color:'#fff', marginBottom:'6px' }}>Your account</div>
                <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.6)', marginBottom:'14px', lineHeight:1.6 }}>View your full order history, receipts and spending analytics in your portal.</div>
                <Link to="/customer/portal" style={{ display:'block', background:TEAL, color:NAVY, textAlign:'center', padding:'11px', borderRadius:'10px', fontSize:'13px', fontWeight:800, textDecoration:'none' }}>
                  Go to my portal →
                </Link>
              </div>
            ) : (
              <div style={{ background:NAVY, borderRadius:'16px', padding:'20px', marginBottom:'14px' }}>
                <div style={{ fontSize:'15px', fontWeight:800, color:'#fff', marginBottom:'6px' }}>Free customer account</div>
                <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.6)', marginBottom:'12px', lineHeight:1.6 }}>Track all your orders, view spending history and keep receipts in one place.</div>
                {[
                  { icon:'rcpt', text:'PDF receipts for every order' },
                  { icon:'hist', text:'Full order history across all sellers' },
                  { icon:'analytics', text:'Spending insights and summaries' },
                  { icon:'bkm', text:'Save your favourite sellers' },
                ].map((f,i)=>(
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
                    <div style={{ width:'22px', height:'22px', borderRadius:'6px', background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <Ic n={f.icon} size={11} color="rgba(255,255,255,0.8)" />
                    </div>
                    <span style={{ fontSize:'12px', color:'rgba(255,255,255,0.8)' }}>{f.text}</span>
                  </div>
                ))}
                <Link to="/customer/signup" style={{ display:'block', background:TEAL, color:NAVY, textAlign:'center', padding:'11px', borderRadius:'10px', fontSize:'13px', fontWeight:800, textDecoration:'none', marginTop:'14px' }}>Create free account →</Link>
                <Link to="/customer/login" style={{ display:'block', textAlign:'center', fontSize:'12px', color:'rgba(255,255,255,0.45)', textDecoration:'none', marginTop:'8px' }}>Already have an account? Sign in</Link>
              </div>
            )}

            {/* Trust signals */}
            <div className="to-side-card">
              <div style={{ fontSize:'13px', fontWeight:700, color:NAVY, marginBottom:'14px' }}>Why customers trust PageConnect</div>
              {[
                { icon:'shld', title:'Verified sellers only',    desc:'Every seller is manually identity-verified.' },
                { icon:'rcpt', title:'Official e-receipts',      desc:'Download PDF receipts for every order.' },
                { icon:'pkg',  title:'Live tracking',            desc:'See your order status update in real time.' },
                { icon:'wa',   title:'Direct seller contact',    desc:'WhatsApp, Facebook or phone instantly.' },
              ].map((f,i)=>(
                <div key={i} style={{ display:'flex', gap:'10px', marginBottom:'13px' }}>
                  <div style={{ width:'34px', height:'34px', borderRadius:'8px', background:'rgba(11,31,58,0.05)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <Ic n={f.icon} size={16} color={NAVY} />
                  </div>
                  <div>
                    <div style={{ fontSize:'12px', fontWeight:700, color:NAVY, marginBottom:'2px' }}>{f.title}</div>
                    <div style={{ fontSize:'11.5px', color:MUTED, lineHeight:1.5 }}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Seller CTA */}
            <div className="to-side-card" style={{ background:'#F0FDF4', border:'1px solid #BBF7D0' }}>
              <div style={{ fontSize:'13px', fontWeight:700, color:'#166534', marginBottom:'5px' }}>Are you a seller?</div>
              <div style={{ fontSize:'12px', color:'#166534', opacity:0.8, marginBottom:'12px', lineHeight:1.6 }}>Join PageConnect to manage orders, track deliveries and build your brand online.</div>
              <a href="/signup" style={{ display:'block', background:GREEN, color:'#fff', textAlign:'center', padding:'10px', borderRadius:'10px', fontSize:'12px', fontWeight:700, textDecoration:'none' }}>Start free 14-day trial</a>
            </div>

            {/* Brand */}
            <div className="to-side-card" style={{ textAlign:'center' }}>
              {brand.logo_url
                ? <img src={brand.logo_url} alt={brand.name} style={{ height:'26px', objectFit:'contain', margin:'0 auto 7px', display:'block' }} />
                : <div style={{ fontSize:'15px', fontWeight:800, color:NAVY, marginBottom:'5px' }}>{brand.name}</div>
              }
              {brand.tagline && <div style={{ fontSize:'12px', color:MUTED, marginBottom:'8px' }}>{brand.tagline}</div>}
              <div style={{ display:'flex', justifyContent:'center', gap:'12px', flexWrap:'wrap' }}>
                {brand.website && <a href={'https://'+brand.website} target="_blank" rel="noopener noreferrer" style={{ fontSize:'11px', color:TEAL_D, textDecoration:'none' }}>{brand.website}</a>}
                {brand.email && <a href={'mailto:'+brand.email} style={{ fontSize:'11px', color:TEAL_D, textDecoration:'none' }}>{brand.email}</a>}
              </div>
              <div style={{ fontSize:'10px', color:MUTED, marginTop:'8px' }}>© 2026 {brand.name}</div>
            </div>
          </div>
        </div>
      </div>

      {receipt && (
        <Receipt
          order={receipt.order} seller={receipt.seller} brand={brand}
          onClose={()=>setReceipt(null)} isLoggedIn={isLoggedIn} navigate={navigate}
        />
      )}
      {showPdfModal && <PdfUnlockModal onClose={() => setShowPdfModal(false)} navigate={navigate} />}
    </div>
  )
}

export default TrackOrder